import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import placeholder from "../../assets/Car-place-holder-PNG.png";
import {
  FileText,
  Download,
  Eye,
  Calendar,
  DollarSign,
  X,
  Plus,
  Check,
  XCircle,
  Upload,
  Clock,
  CreditCard,
} from "lucide-react";
import PaymentPopup from "../common/PaymentPopup";

const BuyerPayment = () => {
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // all, paid, pending
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState("");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicesDetails, setInvoicesDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [childBuyers, setChildBuyers] = useState([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState(null);

  const selectedChildBuyer = React.useMemo(() => {
    if (!selectedBuyerId) return null;
    return childBuyers.find((child) => child.buyerId === selectedBuyerId) || null;
  }, [childBuyers, selectedBuyerId]);

  const selectedBuyerDisplayName =
    selectedChildBuyer?.user?.name || user?.name || "";
  const selectedBuyerDisplayClientId =
    selectedChildBuyer?.user?.clientId || user?.clientId || "";

  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  // Fetch child buyers (corporate buyer dropdown)
  useEffect(() => {
    const fetchChildBuyers = async () => {
      try {
        const response = await api.get("/users/corporatebuyers", {
          params: { limit: 100 },
        });
        const data = response.data?.data || [];
        setChildBuyers(data);
      } catch (error) {
        console.debug("Unable to load corporate child buyers:", error?.message);
        setChildBuyers([]);
      }
    };

    fetchChildBuyers();
  }, []);

  // Fetch buyer invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {};
        if (selectedBuyerId) params.buyerId = selectedBuyerId;

        const response = await api.get("/buyerinvoices/myinvoices", {
          params,
        });

        // Handle different response structures
        const invoicesData = response.data.data || [];

        

        if (!Array.isArray(invoicesData)) {
          console.error("API response data is not an array:", invoicesData);
          setInvoices([]);
          return;
        }

        setInvoices(invoicesData);
      } catch (error) {
        console.error("Error fetching buyer invoices:", error);
        setError(translate("buyerInvoices.messages.noInvoicesFound"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchInvoices();
    }
  }, [user?.id, selectedBuyerId]);

  // useEffect(() => {
  //   fetchInvoicesDetails(invoices?.[0]?.id);
  // }, [invoices]);

  // const fetchInvoicesDetails = async (invoiceId) => {
  //   const response = await api.get(`/buyerinvoicepayments/mypayments/${invoiceId}`);
  //   setInvoicesDetails(response.data.data);
  //   console.log(response.data.data);
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInvoiceStatus = (invoice) => {
    const payments = invoice?.buyerInvoicePayments || [];
    const hasUnderApprovalPayment = payments.some(
      (payment) => payment.isApproved === null && payment.amountPaid > 0
    );

    if (hasUnderApprovalPayment) {
      return {
        text: "Under Approval",
        color: "text-white bg-orange-500",
      };
    }

    // if invoicePaymentStatus is null/undefined treat as pending
    let status =
      invoice.invoicePaymentStatus?.name || invoice.invoicePaymentStatus;
    if (status === null || status === undefined) {
      status = "pending";
    }

    const statusConfig = {
      Paid: {
        text: translate("buyerInvoices.status.paid"),
        color: "text-white bg-green-700",
      },
      paid: {
        text: translate("buyerInvoices.status.paid"),
        color: "text-white bg-green-700",
      },
      Pending: {
        text: translate("buyerInvoices.status.pending"),
        color: "text-white bg-yellow-500",
      },
      pending: {
        text: translate("buyerInvoices.status.pending"),
        color: "text-white bg-yellow-500",
      },
      "Partial Payment": {
        text: translate("buyerInvoices.status.partialPayment"),
        color: "text-white bg-blue-700",
      },
      "partial-payment": {
        text: translate("buyerInvoices.status.partialPayment"),
        color: "text-white bg-blue-700",
      },
      Overdue: {
        text: translate("buyerInvoices.status.overdue"),
        color: "text-white bg-red-700",
      },
      overdue: {
        text: translate("buyerInvoices.status.overdue"),
        color: "text-white bg-red-700",
      },
      Approved: {
        text: translate("buyerInvoices.status.approved"),
        color: "text-white bg-green-700",
      },
      approved: {
        text: translate("buyerInvoices.status.approved"),
        color: "text-white bg-green-700",
      },
      Rejected: {
        text: translate("buyerInvoices.status.rejected"),
        color: "text-white bg-red-700",
      },
      rejected: {
        text: translate("buyerInvoices.status.rejected"),
        color: "text-white bg-red-700",
      },
    };

    return (
      statusConfig[status] || {
        // fallback to pending instead of unknown when no match
        text: translate("buyerInvoices.status.pending"),
        color: "text-white bg-yellow-500",
      }
    );
  };

  const getInvoiceBuyerName = (invoice) => {
    const detail = invoice?.buyerInvoiceDetails?.[0];
    const buyer = detail?.buyerInvoice?.buyer;

    // prefer backend-supplied displayName (for parent viewing child)
    if (detail?.displayName) return detail.displayName;

    if (!buyer) return "-";
    return buyer.user?.name || "-";
  };

  const getInvoiceBuyerClientId = (invoice) => {
    const detail = invoice?.buyerInvoiceDetails?.[0];
    const buyer = detail?.buyerInvoice?.buyer;

    if (detail?.displayClientId) return detail.displayClientId;
    if (!buyer) return "-";
    return buyer.user?.clientId || "-";
  };

  const statusConfig = {
    Paid: {
      text: translate("buyerInvoices.status.paid"),
      color: "text-white bg-green-700",
    },
    paid: {
      text: translate("buyerInvoices.status.paid"),
      color: "text-white bg-green-700",
    },
    "Under Approval": {
      text: "Under Approval",
      color: "text-white bg-orange-500",
    },
    "under-approval": {
      text: "Under Approval",
      color: "text-white bg-orange-500",
    },
    Pending: {
      text: translate("buyerInvoices.status.pending"),
      color: "text-white bg-yellow-500",
    },
    pending: {
      text: translate("buyerInvoices.status.pending"),
      color: "text-white bg-yellow-500",
    },
    "Partial Payment": {
      text: translate("buyerInvoices.status.partialPayment"),
      color: "text-white bg-blue-700",
    },
    "partial-payment": {
      text: translate("buyerInvoices.status.partialPayment"),
      color: "text-white bg-blue-700",
    },
    Overdue: {
      text: translate("buyerInvoices.status.overdue"),
      color: "text-white bg-red-700",
    },
    overdue: {
      text: translate("buyerInvoices.status.overdue"),
      color: "text-white bg-red-700",
    },
    Approved: {
      text: translate("buyerInvoices.status.approved"),
      color: "text-white bg-green-700",
    },
    approved: {
      text: translate("buyerInvoices.status.approved"),
      color: "text-white bg-green-700",
    },
    Rejected: {
      text: translate("buyerInvoices.status.rejected"),
      color: "text-white bg-red-700",
    },
    rejected: {
      text: translate("buyerInvoices.status.rejected"),
      color: "text-white bg-red-700",
    },
  };

  // Fetch real payment history
  const fetchInvoicePayments = async (invoiceId) => {
    try {
      const response = await api.get(
        `/buyerinvoicepayments/mypayments/${invoiceId}`,
      );
      const data = response.data?.data || response.data || [];

      const normalized = data.map((item) => ({
        id: item.id,
        amount: item.amountPaid || 0,
        // Keep as null if it's null, otherwise use the boolean value
        isApproved: item.isApproved === undefined ? null : item.isApproved,
        createdAt: item.createdAt,
        imageUrl: item.imageUrl || null,
        rejectionNote: item.rejectionNote || null,
      }));

      setPaymentRecords(normalized);
    } catch (err) {
      console.error("Failed to load payment history:", err);
      setPaymentRecords([]);
    }
  };

  // 3. Use better effect dependency
  useEffect(() => {
    if (showInvoiceModal && selectedInvoice?.id) {
      setPaymentRecords([]); // clear old data first
      fetchInvoicePayments(selectedInvoice.id);
    }
  }, [showInvoiceModal, selectedInvoice?.id]);
  const getPaymentApprovalStatus = (payment) => {
    if (payment.isApproved === true) {
      return {
        text: translate("buyerInvoices.status.approved") || "Approved",
        color: "bg-green-800/40 text-green-300 border border-green-700/50",
        icon: <Check size={14} className="mr-1" />,
      };
    }

    if (payment.isApproved === false) {
      return {
        text: translate("buyerInvoices.status.rejected") || "Rejected",
        color: "bg-red-800/40 text-red-300 border border-red-700/50",
        icon: <XCircle size={14} className="mr-1" />,
      };
    }

    // null or undefined → pending
    return {
      text: translate("buyerInvoices.status.pending") || "Pending",
      color: "bg-yellow-700/40 text-yellow-300 border border-yellow-600/50",
      icon: <Clock size={14} className="mr-1" />, // you'll need to import Clock from lucide-react
    };
  };

  const filteredInvoices = invoices
    .filter((invoice) => {
      // Filter by status
      if (filterStatus !== "all") {
        const status =
          invoice.invoicePaymentStatus?.name || invoice.invoicePaymentStatus;

        // Map filter status to actual status values
        if (filterStatus === "paid") {
          if (
            !(
              status === "Paid" ||
              status === "paid" ||
              status === "Approved" ||
              status === "approved"
            )
          ) {
            return false;
          }
        } else if (filterStatus === "under-approval") {
          const payments = invoice?.buyerInvoicePayments || [];
          const hasUnderApprovalPayment = payments.some(
            (payment) => payment.isApproved === null && payment.amountPaid > 0
          );
          if (!hasUnderApprovalPayment) {
             return false;
          }
        } else if (filterStatus === "pending") {
          // If it's under approval, don't show it in pending
          const payments = invoice?.buyerInvoicePayments || [];
          const hasUnderApprovalPayment = payments.some(
            (payment) => payment.isApproved === null && payment.amountPaid > 0
          );
          if (hasUnderApprovalPayment) return false;

          if (!(status === "Pending" || status === "pending" || !status)) {
            return false;
          }
        } else if (filterStatus === "partial-payment") {
          if (!(status === "Partial Payment" || status === "partial-payment")) {
            return false;
          }
        } else if (status !== filterStatus) {
          return false;
        }
      }

      // Filter by invoice number search
      if (searchInvoiceNumber && searchInvoiceNumber.trim() !== "") {
        const invoiceNumber = invoice?.id?.toString() || "";
        return invoiceNumber
          .toLowerCase()
          .includes(searchInvoiceNumber.toLowerCase().trim());
      }

      return true;
    })
    .sort((a, b) => {
      // Sort by updatedAt descending (newest first)
      const aDate = new Date(a?.updatedAt || 0);
      const bDate = new Date(b?.updatedAt || 0);

      return bDate - aDate; // Descending order (newest first)
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchInvoiceNumber]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleCloseModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
    setInvoicesDetails([]);
  };

  // Payment popup functions
  const handlePayNow = (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentPopup(true);
  };

  const handleClosePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedPaymentInvoice(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list after successful payment
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48 sm:h-64">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="text-red-600 mb-4 text-sm sm:text-base">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm sm:text-base"
        >
          {translate("buyerInvoices.buttons.close")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="bg-card rounded-lg sm:rounded-xl shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      {/* <div className='font-medium rounded-t-lg sm:rounded-t-[16px] '>
        <div className='p-4 sm:p-5 md:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-primary-soft'>
          <h2 className='text-white sm:text-lg md:text-xl font-semibold'>
            {translate("buyerInvoices.heading")}
          </h2>
        </div>
      </div> */}


            {/* {selectedBuyerDisplayName && (
              <p className="text-xs text-white/80 mt-1">
                {translate("buyerInvoices.labels.currentBuyer") || "Showing for"} {selectedBuyerDisplayName}
                {selectedBuyerDisplayClientId ? ` (${selectedBuyerDisplayClientId})` : ""}
              </p>
            )} */}

            
     <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
  <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[72px]">
    
    {/* Left Side: Heading */}
    <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
      {translate("buyerInvoices.heading")}
    </h2>

    {/* Right Side: Search, Buyer Dropdown, Filter Tabs in a row */}
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
      
      {/* Search Input */}
      <div className="relative w-full sm:w-60 xl:w-60">
        <input
          type="text"
          placeholder={translate("buyerInvoices.searchPlaceholder")}
          className={`w-full px-3 py-1.5 ${isRTL ? "pr-9" : "pl-9"} bg-black/10 border border-white/10 rounded-full text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/30 text-xs transition-all`}
          value={searchInvoiceNumber || ""}
          onChange={(e) => setSearchInvoiceNumber(e.target.value)}
        />
        <svg
          className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-white/70`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Buyer Dropdown */}
      {childBuyers.length > 0 && (
        <select
          value={selectedBuyerId || ""}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedBuyerId(val ? Number(val) : null);
            setCurrentPage(1);
          }}
          className="bg-page p-2 rounded-full text-white text-xs focus:outline-none"
        >
          <option value="">Select Buyer</option>
          {childBuyers.map((child) => (
            <option key={child.buyerId} value={child.buyerId}>
              {child.user?.name || child.businessName || "-"}
              {child.user?.clientId ? ` (${child.user.clientId})` : ""}
            </option>
          ))}
        </select>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center overflow-x-auto no-scrollbar gap-1.5 sm:gap-2">
        {[
          {
            key: "all",
            label: translate("buyerInvoices.filters.all"),
            count: invoices.length,
          },
          {
            key: "paid",
            label: translate("buyerInvoices.filters.paid"),
            count: invoices.filter((inv) =>
              ["Paid", "paid"].includes(inv.invoicePaymentStatus?.name || inv.invoicePaymentStatus)
            ).length,
          },
          {
            key: "pending",
            label: translate("buyerInvoices.filters.pending"),
            count: invoices.filter((inv) => {
               const payments = inv?.buyerInvoicePayments || [];
               const hasUnderApproval = payments.some(p => p.isApproved === null && p.amountPaid > 0);
               return !hasUnderApproval && ["Pending", "pending", undefined, null].includes(inv.invoicePaymentStatus?.name || inv.invoicePaymentStatus);
            }).length,
          },
          {
            key: "under-approval",
            label: "Under Approval",
            count: invoices.filter((inv) => {
               const payments = inv?.buyerInvoicePayments || [];
               return payments.some(p => p.isApproved === null && p.amountPaid > 0);
            }).length,
          },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilterStatus(tab.key)}
            className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 flex items-center gap-1.5 ${filterStatus === tab.key
              ? "bg-white text-primary shadow-md"
              : "bg-black/10 text-white/80 hover:bg-white/10"
            }`}
          >
            <span>{tab.label}</span>
            <span className="text-[9px] opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

    </div>
  </div>
</div>

      <div className="space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-5 py-3 sm:py-4">
        {/* filter and search bar */}
        {/* <div className='border-b border-default'>      
          <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 py-3 sm:py-4'>
            <nav className='flex space-x-1.5 sm:space-x-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide'>
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "all"
                    ? "bg-gradient-to-r from-primary to-secondary text-white shadow-md"
                    : "bg-secondary text-white hover:bg-primary hover:text-white"
                }`}
              >
                {translate("buyerInvoices.filters.all")} ({invoices.length})
              </button>
              <button
                onClick={() => setFilterStatus("paid")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "paid"
                    ? "bg-gradient-to-r from-primary to-secondary text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.paid")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return (
                      status === "Paid" ||
                      status === "paid" ||
                      status === "Approved" ||
                      status === "approved"
                    );
                  }).length
                }
                )
              </button>
              <button
                onClick={() => setFilterStatus("pending")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "pending"
                    ? "bg-gradient-to-r from-primary to-secondary text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.pending")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return status === "Pending" || status === "pending";
                  }).length
                }
                )
              </button>
              <button
                onClick={() => setFilterStatus("partial-payment")}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  filterStatus === "partial-payment"
                    ? "bg-gradient-to-r from-primary to-secondary text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                }`}
              >
                {translate("buyerInvoices.filters.partialPayment")} (
                {
                  invoices.filter((inv) => {
                    const status =
                      inv.invoicePaymentStatus?.name ||
                      inv.invoicePaymentStatus;
                    return (
                      status === "Partial Payment" ||
                      status === "partial-payment"
                    );
                  }).length
                }
                )
              </button>
            </nav>

         
            <div className='flex items-center w-full lg:w-auto'>
              <div className='relative w-full lg:w-64 xl:w-80'>
                <input
                  type='text'
                  placeholder={translate("buyerInvoices.searchPlaceholder")}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 ${
                    isRTL ? "pr-9 sm:pr-10" : "pl-9 sm:pl-10"
                  } border border-default bg-page rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-xs sm:text-sm`}
                  value={searchInvoiceNumber || ""}
                  onChange={(e) => setSearchInvoiceNumber(e.target.value)}
                />
                <svg
                  className={`absolute ${
                    isRTL ? "right-2.5 sm:right-3" : "left-2.5 sm:left-3"
                  } top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              {searchInvoiceNumber && (
                <button
                  onClick={() => setSearchInvoiceNumber("")}
                  className='ml-2 p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors'
                  title='Clear search'
                >
                  <svg
                    className='w-4 h-4 sm:w-5 sm:h-5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div> */}

        {/* Invoices List */}
        <div className="space-y-3 sm:space-y-4">
          {paginatedInvoices.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-white sm:text-lg font-medium mb-2">
                {translate("buyerInvoices.messages.noInvoicesFound")}
              </h3>
              <p className=" text-description px-4">
                {filterStatus === "all"
                  ? translate("buyerInvoices.messages.noInvoicesYet")
                  : translate(
                    "buyerInvoices.messages.noFilteredInvoices",
                  ).replace(
                    "{status}",
                    translate(`buyerInvoices.filters.${filterStatus}`),
                  )}
              </p>
            </div>
          ) : (
            paginatedInvoices.map((invoice) => {
              const status = getInvoiceStatus(invoice);
              const buyerDetail = invoice?.buyerInvoiceDetails?.[0];
              const isChildRecord = !!buyerDetail?.buyerInvoice?.buyer?.parentId;

              const underApprovalAmount = (invoice?.buyerInvoicePayments || [])
                .filter((p) => p.isApproved === null && p.amountPaid > 0)
                .reduce((sum, p) => sum + (p.amountPaid || 0), 0);

              return (
                <div
                  key={invoice.id}
                  className={`border border-gray-800/50 rounded-xl shadow-sm p-4 sm:p-5 hover:shadow-lg hover:shadow-black/30 transition-all duration-200 ${isChildRecord ? "bg-gray-900/40" : "bg-page"
                    }`}
                >
                  <div className="block lg:hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                        <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <h3 className="text-base font-semibold text-gray-100 truncate">
                          {translate("buyerInvoices.labels.invoiceNumber")}{" "}
                          {invoice.id}
                        </h3>
                        <p className="text-xs text-gray-300 mt-1">
                          {translate("buyerInvoices.labels.buyer") || "Buyer"}: {getInvoiceBuyerName(invoice)} ({getInvoiceBuyerClientId(invoice)})
                        </p>
                      </div>
                      <span
                        className={`px-2.5 py-1.5 rounded-full text-xs font-medium flex-shrink-0 ${status.color}`}
                      >
                        {status.text}
                      </span>
                    </div>

                    {/* Vehicle + Basic Info */}
                    <div className="flex items-start space-x-2 mb-4">
                      <div className="w-24 h-24 bg-bg-blue rounded-lg overflow-hidden flex-shrink-0 border border-gray-800/60">
                        {invoice?.buyerInvoiceDetails?.[0]?.vehicle?.images?.[0]
                          ?.imageUrl ? (
                          <img
                            src={
                              invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                ?.images?.[0]?.imageUrl || placeholder
                            }
                            alt="Vehicle"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = placeholder;
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-bg-light flex items-center justify-center">
                            <span className="text-gray-600 text-xs">
                              No image
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-gray-100 leading-tight">
                          {invoice?.buyerInvoiceDetails?.[0]?.vehicle?.name ||
                            "—"}
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          {translate("buyerInvoices.labels.lotNo")}{" "}
                          {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                            ?.lotNumber || "—"}
                        </p>
                      </div>
                    </div>

                    {/* Amount Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div className="bg-bg-medium rounded-lg p-3 border border-gray-800/50">
                        <p className="text-xs text-gray-400 mb-1">
                          {translate("buyerInvoices.labels.totalAmount")}
                        </p>
                        <p className="text-lg font-semibold text-blue-400">
                          {displayPriceWithSymbol(
                            convertPrice(invoice?.totalAmount || 0),
                          )}
                        </p>
                      </div>

                      {underApprovalAmount > 0 && (
                        <div className="bg-bg-medium rounded-lg p-3 border border-gray-800/50">
                          <p className="text-xs text-gray-400 mb-1">Under Approval</p>
                          <p className="text-lg font-semibold text-orange-400">
                            {displayPriceWithSymbol(
                              convertPrice(underApprovalAmount),
                            )}
                          </p>
                        </div>
                      )}

                      <div className="bg-bg-medium rounded-lg p-3 border border-gray-800/50">
                        <p className="text-xs text-gray-400 mb-1">
                          {translate("buyerInvoices.labels.paidAmount")}
                        </p>
                        <p className="text-lg font-semibold text-emerald-500">
                          {displayPriceWithSymbol(
                            convertPrice(
                              (invoice?.totalAmount || 0) -
                              (invoice?.remainingAmount || 0),
                            ),
                          )}
                        </p>
                      </div>

                      <div className="bg-bg-medium rounded-lg p-3 border border-gray-800/50">
                        <p className="text-xs text-gray-400 mb-1">Pending</p>
                        <p className="text-xs font-semibold text-orange-500">
                          {displayPriceWithSymbol(
                            convertPrice(invoice?.remainingAmount || 0),
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="bg-bg-medium rounded-lg p-3 mb-4 border border-gray-800/50">
                      <p className="text-xs text-gray-400 mb-1">
                        {translate("buyerInvoices.labels.invoiceDate")}
                      </p>
                      <p className="text-sm font-medium text-gray-200">
                        {invoice?.createdAt
                          ? new Date(invoice.createdAt).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            },
                          )
                          : "—"}
                      </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-col gap-3">
                      {(status.text === "Pending" ||
                        status.text === "Partial Payment") && (
                          <button
                            onClick={() => handlePayNow(invoice)}
                            className="w-full bg-primary hover:bg-primary-hover text-white px-5 py-3 rounded-lg transition-colors font-medium shadow-sm"
                          >
                            {translate("buyerInvoices.buttons.payNow")}
                          </button>
                        )}

                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className={`w-full flex items-center justify-center bg-light hover:bg-gray-800 text-gray-100 px-5 py-3 rounded-lg transition-colors font-medium border border-gray-700 ${isRTL ? "flex-row-reverse" : ""}`}
                      >
                        <Eye className={`w-5 h-5 ${isRTL ? "ml-2" : "mr-2"}`} />
                        {translate("buyerInvoices.buttons.viewDetails")}
                      </button>
                    </div>
                  </div>

                  <div className="hidden lg:block">
                    <div className="flex items-start justify-between gap-2">
                      {/* Left - Main Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="flex items-center gap-2.5">
                            <FileText className="w-5 h-5 text-gray-500" />
                            <h3 className="text-lg font-semibold text-gray-100">
                              {translate("buyerInvoices.labels.invoiceNumber")}{" "}
                              {invoice.id}
                            </h3>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${status.color}`}
                          >
                            {status.text}
                          </span>
                        </div>

                        <div className="bg-bg-medium rounded-lg p-4 border border-gray-800/60">
                          <div className="flex items-center gap-4 lg:gap-5 xl:gap-6">
                            {/* 1. Vehicle image – 4:3 landscape */}
                            <div className="flex-shrink-0">
                              <div className="w-40 h-30 rounded-lg overflow-hidden border border-gray-700/60 bg-bg-light">
                                {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                  ?.images?.[0]?.imageUrl ? (
                                  <img
                                    src={
                                      invoice.buyerInvoiceDetails[0].vehicle
                                        .images[0].imageUrl || placeholder
                                    }
                                    onError={(e) =>
                                      (e.target.src = placeholder)
                                    }
                                    alt="Vehicle"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                                    No image
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Name + Lot */}
                            <div className="col-span-4">
                              <p className="font-medium text-gray-100">
                                {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                  ?.name || "—"}
                              </p>
                              {invoice?.buyerInvoiceDetails?.map((detail) => {
                                const childName =
                                  detail.buyerInvoice?.buyer?.user?.name;
                                const clientId =
                                  detail.displayClientId || detail.buyerInvoice?.buyer?.user?.clientId || "-";



                                return (
                                  childName && (
                                    <p
                                      key={detail.id}
                                      className="text-sm text-gray-300"
                                    >
                                      {translate(
                                        "wonVehicles.labels.childName",
                                      )}
                                      <span className="text-sm text-primary">
                                        {childName} ({clientId})
                                      </span>
                                    </p>
                                  )
                                );
                              })}
                              <p className="text-sm text-gray-400 mt-0.5">
                                Lot #
                                {invoice?.buyerInvoiceDetails?.[0]?.vehicle
                                  ?.lotNumber || "—"}
                              </p>
                            </div>

                            {/* 3. Total */}
                            <div className="flex-shrink-0 text-center min-w-[90px] lg:min-w-[100px]">
                              <p className="text-xs text-gray-400 uppercase tracking-wide">
                                Total
                              </p>
                              <p className="font-semibold text-blue-400 mt-1 text-[15px] lg:text-base">
                                {displayPriceWithSymbol(
                                  convertPrice(invoice?.totalAmount || 0),
                                )}
                              </p>
                            </div>

                            {/* Under Approval */}
                            {underApprovalAmount > 0 && (
                              <div className="flex-shrink-0 text-center min-w-[90px] lg:min-w-[100px]">
                                <p className="text-xs text-gray-400 uppercase tracking-wide">
                                  Under Approval
                                </p>
                                <p className="font-semibold text-orange-400 mt-1 text-[15px] lg:text-base">
                                  {displayPriceWithSymbol(
                                    convertPrice(underApprovalAmount),
                                  )}
                                </p>
                              </div>
                            )}

                            {/* 4. Paid */}
                            <div className="flex-shrink-0 text-center min-w-[90px] lg:min-w-[100px]">
                              <p className="text-xs text-gray-400 uppercase tracking-wide">
                                Paid
                              </p>
                              <p className="font-semibold text-emerald-500 mt-1 text-[15px] lg:text-base">
                                {displayPriceWithSymbol(
                                  convertPrice(
                                    (invoice?.totalAmount || 0) -
                                    (invoice?.remainingAmount || 0),
                                  ),
                                )}
                              </p>
                            </div>

                            {/* 5. Pending – highlighted */}
                            <div className="flex-shrink-0 text-center min-w-[110px] lg:min-w-[120px] px-3 py-2.5  rounded-lg  ">
                              <p className="text-xs text-orange-300/90 uppercase tracking-wide">
                                Pending
                              </p>
                              <p className="font-bold text-orange-400 mt-1 text-[15px] lg:text-base">
                                {displayPriceWithSymbol(
                                  convertPrice(invoice?.remainingAmount || 0),
                                )}
                              </p>
                            </div>

                            {/* 6. Date */}
                            <div className="flex-shrink-0 text-center min-w-[130px] lg:min-w-[150px]">
                              <p className="text-xs text-gray-400 uppercase tracking-wide">
                                Date
                              </p>
                              <p className="text-sm font-medium text-gray-200 mt-1.5 whitespace-nowrap">
                                {invoice?.createdAt
                                  ? new Date(
                                    invoice.createdAt,
                                  ).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })
                                  : "—"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right - Actions */}
                      <div className="flex  gap-2">
                        {(status.text === "Pending" ||
                          status.text === "Partial Payment") && (
                            <div className="relative h-10 w-10">
                              <button
                                onClick={() => handlePayNow(invoice)}
                                className="absolute right-0 top-0 h-10 w-10 hover:w-40 flex items-center bg-blue-600 border border-blue-500 hover:bg-blue-700 text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                              >
                                {/* Wrapper with fixed layout */}
                                <div className="flex items-center h-full w-full">
                                  {/* Icon Container: Always centered */}
                                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                    <CreditCard size={20} />
                                  </div>

                                  {/* Text: Slides out */}
                                  <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                    {translate("buyerInvoices.buttons.payNow")}
                                  </span>
                                </div>
                              </button>
                            </div>
                          )}

                        <div className="relative h-10 w-10">
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            className="absolute right-0 top-0 h-10 w-10 hover:w-44 flex items-center bg-gray-800 border border-gray-700 hover:bg-gray-700 text-gray-100 rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                          >
                            {/* Wrapper with fixed layout */}
                            <div
                              className={`flex items-center h-full w-full ${isRTL ? "flex-row-reverse" : ""}`}
                            >
                              {/* Icon Container: Always centered in the first 40px */}
                              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                <FileText size={20} />
                              </div>

                              {/* Text: Slides out */}
                              <span className="px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                {translate("buyerInvoices.buttons.viewDetails")}
                              </span>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-4 sm:mt-6 gap-3 sm:gap-0">
            <div
              className={`text-xs sm:text-sm text-gray-700 text-center ${isRTL ? "sm:text-right" : "sm:text-left"
                }`}
            >
              {translate("buyerInvoices.pagination.showing")} {startIndex + 1}{" "}
              {translate("buyerInvoices.pagination.to")}{" "}
              {Math.min(endIndex, filteredInvoices.length)}{" "}
              {translate("buyerInvoices.pagination.of")}{" "}
              {filteredInvoices.length}{" "}
              {translate("buyerInvoices.pagination.invoices")}
            </div>
            <div
              className={`flex items-center ${isRTL ? "space-x-reverse" : "space-x-1"
                } sm:space-x-2`}
            >
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translate("buyerInvoices.pagination.previous")}
              </button>

              <div className="flex space-x-1 overflow-x-auto">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md flex-shrink-0 ${currentPage === page
                        ? "bg-primary text-white"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translate("buyerInvoices.pagination.next")}
              </button>
            </div>
          </div>
        )}

        {/* Invoice Payment Modal */}
        {showInvoiceModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-bg-dark rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[96vh] sm:max-h-[92vh] overflow-y-auto border border-gray-800/60">
              {/* Header */}
              <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-800/70">
                <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-100">
                  {translate("buyerInvoices.labels.paymentHistory")} #
                  {selectedInvoice?.id}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-300 transition-colors p-1.5 rounded-full hover:bg-bg-medium/60"
                >
                  <X className="w-6 h-6 sm:w-7 sm:h-7" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 space-y-6 sm:space-y-8">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
                  <div className="bg-bg-medium rounded-xl p-4 sm:p-5 border border-gray-800/50">
                    <h4 className="text-sm sm:text-base font-medium text-gray-400 mb-2">
                      {translate("buyerInvoices.labels.totalAmount")}
                    </h4>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-400 tracking-tight">
                      {displayPriceWithSymbol(
                        convertPrice(selectedInvoice?.totalAmount || 0),
                      )}
                    </p>
                  </div>

                  <div className="bg-bg-medium rounded-xl p-4 sm:p-5 border border-gray-800/50">
                    <h4 className="text-sm sm:text-base font-medium text-gray-400 mb-2">
                      {translate("buyerInvoices.labels.remainingAmount")}
                    </h4>
                    <p className="text-2xl sm:text-3xl font-bold text-red-500/90">
                      {displayPriceWithSymbol(
                        convertPrice(selectedInvoice?.remainingAmount || 0),
                      )}
                    </p>
                  </div>

                  {/* Optional third card – Paid Amount */}
                  <div className="bg-bg-medium rounded-xl p-4 sm:p-5 border border-gray-800/50 hidden sm:block">
                    <h4 className="text-sm sm:text-base font-medium text-gray-400 mb-2">
                      Paid
                    </h4>
                    <p className="text-2xl sm:text-3xl font-bold text-emerald-500/90">
                      {displayPriceWithSymbol(
                        convertPrice(
                          (selectedInvoice?.totalAmount || 0) -
                          (selectedInvoice?.remainingAmount || 0),
                        ),
                      )}
                    </p>
                  </div>
                </div>

                {/* Payment History Section */}

                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-100">
                      {translate("buyerInvoices.labels.paymentHistory")}
                    </h4>

                    <span className="text-sm text-gray-400">
                      {paymentRecords.length}{" "}
                      {paymentRecords.length === 1 ? "payment" : "payments"}
                    </span>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-gray-800/60">
                    <table className="min-w-full divide-y divide-gray-800/70">
                      <thead className="bg-bg-medium/70">
                        <tr>
                          <th
                            className={`px-5 py-3.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : ""}`}
                          >
                            ID / Receipt
                          </th>
                          <th
                            className={`px-5 py-3.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : ""}`}
                          >
                            Amount
                          </th>
                          <th
                            className={`px-5 py-3.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : ""} w-44 min-w-[11rem]`}
                          >
                            Status
                          </th>
                          <th
                            className={`px-5 py-3.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : ""}`}
                          >
                            Date
                          </th>
                          <th className="px-5 py-3.5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            Receipt
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-800/50 bg-bg-dark/60">
                        {paymentRecords.length > 0 ? (
                          paymentRecords
                            .sort(
                              (a, b) =>
                                new Date(b.createdAt) - new Date(a.createdAt),
                            )
                            .map((payment) => {
                              const status =
                                payment.isApproved === true
                                  ? {
                                    text:
                                      translate(
                                        "buyerInvoices.status.approved",
                                      ) || "Approved",
                                    color:
                                      "bg-green-900/40 text-green-300 border border-green-700/50",
                                    icon: (
                                      <Check size={14} className="mr-1.5" />
                                    ),
                                  }
                                  : payment.isApproved === false
                                    ? {
                                      text:
                                        translate(
                                          "buyerInvoices.status.rejected",
                                        ) || "Rejected",
                                      color:
                                        "bg-red-900/40 text-red-300 border border-red-700/50",
                                      icon: (
                                        <XCircle
                                          size={14}
                                          className="mr-1.5"
                                        />
                                      ),
                                    }
                                    : payment.isApproved === null && payment.amount > 0
                                      ? {
                                        text: "Under Approval",
                                        color: "bg-orange-500/40 text-orange-200 border border-orange-500/50",
                                        icon: <Clock size={14} className="mr-1.5" />
                                      }
                                      : {
                                        // This captures null or undefined without amount (Pending)
                                        text:
                                          translate(
                                            "buyerInvoices.status.pending",
                                          ) || "Pending",
                                        color:
                                          "bg-yellow-700/40 text-yellow-300 border border-yellow-600/50",
                                        icon: (
                                          <Clock size={14} className="mr-1.5" />
                                        ),
                                      };

                              return (
                                <tr
                                  key={payment.id}
                                  className="hover:bg-bg-medium/40 transition-colors"
                                >
                                  <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-gray-200">
                                    #{payment.id}
                                  </td>

                                  <td className="px-5 py-4 whitespace-nowrap text-sm font-semibold text-emerald-400">
                                    {displayPriceWithSymbol(
                                      convertPrice(payment.amount || 0),
                                    )}
                                  </td>

                                  <td className="px-5 py-4">
                                    <div className="flex flex-col gap-1.5">
                                      <span
                                        className={`inline-flex items-center px-3.5 py-1 rounded-full text-xs font-medium ${status.color}`}
                                      >
                                        {status.icon}
                                        {status.text}
                                      </span>

                                      {payment.isApproved === false &&
                                        payment.rejectionNote && (
                                          <div className="text-xs text-red-400/90 mt-1 max-w-[260px] break-words leading-relaxed">
                                            <span className="font-medium">
                                              Reason:{" "}
                                            </span>
                                            {payment.rejectionNote}
                                          </div>
                                        )}
                                    </div>
                                  </td>

                                  <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-300">
                                    {formatDate(payment.createdAt)}
                                  </td>

                                  <td className="px-5 py-4 whitespace-nowrap text-center">
                                    {payment.imageUrl ? (
                                      <a
                                        href={payment.imageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm font-medium hover:underline transition-colors"
                                      >
                                        <Eye size={16} className="mr-1.5" />
                                        View
                                      </a>
                                    ) : (
                                      <span className="text-gray-500 text-sm">
                                        —
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-6 py-16 text-center">
                              <div className="flex flex-col items-center text-gray-500">
                                <DollarSign
                                  size={40}
                                  className="mb-3 opacity-50"
                                />
                                <p className="text-base font-medium">
                                  No payments recorded yet
                                </p>
                                <p className="text-sm mt-1.5 opacity-80">
                                  {selectedInvoice?.remainingAmount > 0
                                    ? "Make a payment to see it here"
                                    : "All payments completed"}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end p-5 sm:p-6 border-t border-gray-800/70">
                <button
                  onClick={handleCloseModal}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors font-medium shadow-sm"
                >
                  {translate("buyerInvoices.buttons.close")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Popup */}
      <PaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePaymentPopup}
        selectedItem={selectedPaymentInvoice}
        itemType="invoice"
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default BuyerPayment;
